import { QuizzesItemType, quizzesItemTypes } from '../types';

type UniqueIdFactory = {
  [key in QuizzesItemType]: {
    toUniqueId: (id: number) => string;
    toEntityId: (entityId: string) => number;
  };
};
export const matchUniqueIdToEntityId = (str: string) => {
  if (!str) {
    return {
      itemType: null,
      entityId: null,
    };
  }

  const match = str.match(`(${quizzesItemTypes.join('|')})-(\\d+)`);

  if (!match) {
    return {
      itemType: null,
      entityId: null,
    };
  } else {
    // str.match returns an array with the first element being the entire match,
    // and the following elements being the contents of the capture groups.
    const [itemType, entityId] = match.slice(1);
    return {
      itemType: itemType as QuizzesItemType,
      entityId: +entityId,
    };
  }
};

/**
 * Because folders and quizzes use IDs that could conflict, we need to generate
 *  more globally unique names for them.
 *  We do that by appending `folder` or `Quiz` infront of them
 */
export const uniqueIdFactory: UniqueIdFactory =
  quizzesItemTypes.reduce<UniqueIdFactory>(
    (factory: UniqueIdFactory, currentItemType) => {
      const toUniqueId = (entityId: number) => `${currentItemType}-${entityId}`;
      const toEntityId = (uniqueId: string): number => {
        const { entityId } = matchUniqueIdToEntityId(uniqueId);
        return entityId;
      };
      factory[currentItemType] = {
        toUniqueId: toUniqueId,
        toEntityId: toEntityId,
      };
      return factory;
    },
    {} as UniqueIdFactory
  );

export const uniqueIdToEntityId = (uniqueId: string) => {
  const { itemType, entityId } = matchUniqueIdToEntityId(uniqueId);
  if (quizzesItemTypes.find((quizItemType) => quizItemType === itemType)) {
    return [itemType, entityId] as const;
  }
};

export const reduceUniqueIdsToObject = (uniqueIds: string[]) =>
  uniqueIds.map(matchUniqueIdToEntityId).reduce(
    (entitiesIds, { itemType, entityId }) => {
      entitiesIds[itemType] = entitiesIds[itemType].add(entityId);
      return entitiesIds;
    },
    { folder: new Set(), quiz: new Set() } as Record<
      QuizzesItemType,
      Set<number>
    >
  );

export const itemTypeIs =
  (quizItemType: QuizzesItemType) => (uniqueId: string) => {
    const [itemType] = uniqueIdToEntityId(uniqueId);
    return itemType === quizItemType;
  };
